import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Command, Search, Zap, Shield, TrendingUp, RefreshCw, PieChart,
  Wallet, BarChart3, AlertTriangle, ChevronRight, Loader2,
  Sparkles, ArrowUpRight, Clock, FileText, Settings,
  ArrowDownUp, Building2, DollarSign, Activity, Play, Send, CheckCircle,
  Upload, Users, Briefcase, Globe, Calendar, Receipt, Brain
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { agenticApi } from '../../services/agenticApi';
import type { AgenticBundle, IntentSuggestion } from '../../types/agentic';

// ============================================================================
// TYPES
// ============================================================================
export interface CommandAction {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  category: CommandCategory;
  keywords: string[];
  shortcut?: string;
  action: () => void | Promise<void>;
  requiresConfirmation?: boolean;
  parameters?: CommandParameter[];
}

export interface CommandParameter {
  id: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'portfolio' | 'security' | 'date';
  required: boolean;
  options?: { value: string; label: string }[];
  placeholder?: string;
}

export type CommandCategory =
  | 'trading'
  | 'workflow'
  | 'compliance'
  | 'portfolio'
  | 'ibor'
  | 'middle-office'
  | 'reporting'
  | 'system';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onExecute: (action: CommandAction, params?: Record<string, any>) => Promise<void>;
  onIntentResult?: (bundle: AgenticBundle) => void;
  recentCommands?: string[];
  customActions?: CommandAction[];
  portfolios?: { id: number; name: string }[];
}

type PaletteMode = 'command' | 'ai';

// ============================================================================
// OMS-SPECIFIC COMMANDS
// ============================================================================
const defaultCommands: Omit<CommandAction, 'action'>[] = [
  // Trading Commands
  {
    id: 'new-trade',
    title: 'New Trade',
    description: 'Create a new bond trade entry',
    icon: <ArrowUpRight className="w-4 h-4" />,
    category: 'trading',
    keywords: ['trade', 'buy', 'sell', 'order', 'new', 'create'],
    shortcut: 'N',
    parameters: [
      { id: 'security', label: 'Security', type: 'security', required: true, placeholder: 'Search CUSIP or name...' },
      { id: 'side', label: 'Side', type: 'select', required: true, options: [{ value: 'BUY', label: 'Buy' }, { value: 'SELL', label: 'Sell' }] },
      { id: 'quantity', label: 'Quantity', type: 'number', required: true, placeholder: 'Face value...' },
      { id: 'portfolio', label: 'Portfolio', type: 'portfolio', required: true },
    ]
  },
  {
    id: 'block-trade',
    title: 'Block Trade',
    description: 'Create block trade with allocations across portfolios',
    icon: <Briefcase className="w-4 h-4" />,
    category: 'trading',
    keywords: ['block', 'allocation', 'multi-portfolio'],
    shortcut: 'B',
  },
  {
    id: 'quick-rfq',
    title: 'Request Quote (RFQ)',
    description: 'Request for quote from trading venues',
    icon: <Zap className="w-4 h-4" />,
    category: 'trading',
    keywords: ['rfq', 'quote', 'price', 'bid', 'offer'],
    shortcut: 'Q',
  },

  // Workflow Commands (Trade Lifecycle)
  {
    id: 'submit-preclear',
    title: 'Submit for Pre-Clear',
    description: 'Submit selected trades for compliance pre-clearance',
    icon: <Send className="w-4 h-4" />,
    category: 'workflow',
    keywords: ['submit', 'preclear', 'compliance', 'draft'],
    shortcut: 'P',
  },
  {
    id: 'approve-trades',
    title: 'Approve Trades',
    description: 'Approve pending trades for execution',
    icon: <CheckCircle className="w-4 h-4" />,
    category: 'workflow',
    keywords: ['approve', 'authorize', 'pending'],
    shortcut: 'A',
  },
  {
    id: 'send-to-venue',
    title: 'Send to Venue',
    description: 'Send approved trades to execution venues (FIX)',
    icon: <Globe className="w-4 h-4" />,
    category: 'workflow',
    keywords: ['send', 'execute', 'venue', 'fix', 'tradeweb', 'marketaxess'],
    shortcut: 'S',
  },
  {
    id: 'post-trades',
    title: 'Post to IBOR',
    description: 'Post executed trades to Investment Book of Record',
    icon: <Upload className="w-4 h-4" />,
    category: 'workflow',
    keywords: ['post', 'ibor', 'book', 'settle'],
  },

  // Compliance Commands
  {
    id: 'compliance-check',
    title: 'Run Compliance Check',
    description: 'Validate trades against compliance rules',
    icon: <Shield className="w-4 h-4" />,
    category: 'compliance',
    keywords: ['compliance', 'check', 'validate', 'rules'],
    shortcut: 'C',
  },
  {
    id: 'view-violations',
    title: 'View Violations',
    description: 'Show active compliance violations',
    icon: <AlertTriangle className="w-4 h-4" />,
    category: 'compliance',
    keywords: ['violations', 'breach', 'alert', 'warning'],
  },
  {
    id: 'manage-rules',
    title: 'Manage Rules',
    description: 'Configure compliance rules and thresholds',
    icon: <Settings className="w-4 h-4" />,
    category: 'compliance',
    keywords: ['rules', 'configure', 'threshold', 'limit'],
  },

  // Portfolio Commands
  {
    id: 'ai-intent',
    title: 'AI Intent',
    description: 'Describe a portfolio objective in natural language',
    icon: <Brain className="w-4 h-4" />,
    category: 'portfolio',
    keywords: ['ai', 'intent', 'natural', 'language', 'smart', 'goal', 'objective'],
    shortcut: '/',
  },
  {
    id: 'rebalance',
    title: 'Rebalance Portfolio',
    description: 'Generate rebalancing trades to match targets',
    icon: <RefreshCw className="w-4 h-4" />,
    category: 'portfolio',
    keywords: ['rebalance', 'target', 'drift', 'allocation'],
    shortcut: 'R',
    parameters: [
      { id: 'portfolio', label: 'Portfolio', type: 'portfolio', required: true },
    ]
  },
  {
    id: 'hedge-portfolio',
    title: 'Hedge Portfolio',
    description: 'Generate hedging recommendations',
    icon: <Shield className="w-4 h-4" />,
    category: 'portfolio',
    keywords: ['hedge', 'protect', 'risk', 'duration'],
  },
  {
    id: 'tax-harvest',
    title: 'Tax Loss Harvest',
    description: 'Find tax loss harvesting opportunities',
    icon: <DollarSign className="w-4 h-4" />,
    category: 'portfolio',
    keywords: ['tax', 'harvest', 'loss', 'wash'],
  },
  {
    id: 'portfolio-analysis',
    title: 'Analyze Portfolio',
    description: 'Run comprehensive portfolio analytics',
    icon: <PieChart className="w-4 h-4" />,
    category: 'portfolio',
    keywords: ['analyze', 'analytics', 'risk', 'performance'],
  },

  // IBOR Commands
  {
    id: 'view-positions',
    title: 'View Positions',
    description: 'Open positions screen with current holdings',
    icon: <BarChart3 className="w-4 h-4" />,
    category: 'ibor',
    keywords: ['positions', 'holdings', 'book'],
  },
  {
    id: 'view-tax-lots',
    title: 'View Tax Lots',
    description: 'Show tax lot details and cost basis',
    icon: <Receipt className="w-4 h-4" />,
    category: 'ibor',
    keywords: ['tax', 'lots', 'cost', 'basis', 'fifo', 'lifo'],
  },
  {
    id: 'mark-to-market',
    title: 'Mark to Market',
    description: 'Update position valuations',
    icon: <TrendingUp className="w-4 h-4" />,
    category: 'ibor',
    keywords: ['mtm', 'valuation', 'mark', 'price'],
  },

  // Middle Office Commands
  {
    id: 'reconciliation',
    title: 'Run Reconciliation',
    description: 'Reconcile positions with custodian',
    icon: <ArrowDownUp className="w-4 h-4" />,
    category: 'middle-office',
    keywords: ['reconcile', 'custodian', 'breaks', 'match'],
  },
  {
    id: 'corporate-actions',
    title: 'Corporate Actions',
    description: 'Process pending corporate actions',
    icon: <Calendar className="w-4 h-4" />,
    category: 'middle-office',
    keywords: ['corporate', 'actions', 'coupon', 'maturity', 'call'],
  },
  {
    id: 'cash-management',
    title: 'Cash Management',
    description: 'View and manage cash positions',
    icon: <Wallet className="w-4 h-4" />,
    category: 'middle-office',
    keywords: ['cash', 'sweep', 'forecast', 'overdraft'],
  },
  {
    id: 'settlement-status',
    title: 'Settlement Status',
    description: 'Check trade settlement status',
    icon: <Clock className="w-4 h-4" />,
    category: 'middle-office',
    keywords: ['settlement', 'fail', 'pending', 't+1', 't+2'],
  },

  // Reporting Commands
  {
    id: 'generate-report',
    title: 'Generate Report',
    description: 'Create regulatory or client report',
    icon: <FileText className="w-4 h-4" />,
    category: 'reporting',
    keywords: ['report', 'regulatory', 'mifid', '13f', 'pf'],
  },
  {
    id: 'audit-log',
    title: 'View Audit Log',
    description: 'Show activity audit trail',
    icon: <FileText className="w-4 h-4" />,
    category: 'reporting',
    keywords: ['audit', 'log', 'history', 'trail'],
  },
  {
    id: 'ai-audit-log',
    title: 'AI Decision Log',
    description: 'Show AI suggestions and decisions audit',
    icon: <Sparkles className="w-4 h-4" />,
    category: 'reporting',
    keywords: ['ai', 'audit', 'llm', 'decisions'],
  },

  // System Commands
  {
    id: 'fix-monitor',
    title: 'FIX Monitor',
    description: 'Monitor FIX session status and messages',
    icon: <Activity className="w-4 h-4" />,
    category: 'system',
    keywords: ['fix', 'session', 'monitor', 'connection'],
  },
  {
    id: 'user-management',
    title: 'User Management',
    description: 'Manage users and permissions',
    icon: <Users className="w-4 h-4" />,
    category: 'system',
    keywords: ['users', 'permissions', 'roles', 'admin'],
  },
  {
    id: 'settings',
    title: 'Settings',
    description: 'Open application settings',
    icon: <Settings className="w-4 h-4" />,
    category: 'system',
    keywords: ['settings', 'preferences', 'config'],
    shortcut: ',',
  },
];

const categoryLabels: Record<CommandCategory, string> = {
  trading: 'Trading',
  workflow: 'Trade Workflow',
  compliance: 'Compliance',
  portfolio: 'Portfolio',
  ibor: 'IBOR',
  'middle-office': 'Middle Office',
  reporting: 'Reporting',
  system: 'System',
};

const categoryIcons: Record<CommandCategory, React.ReactNode> = {
  trading: <ArrowUpRight className="w-3.5 h-3.5" />,
  workflow: <Send className="w-3.5 h-3.5" />,
  compliance: <Shield className="w-3.5 h-3.5" />,
  portfolio: <Wallet className="w-3.5 h-3.5" />,
  ibor: <BarChart3 className="w-3.5 h-3.5" />,
  'middle-office': <Building2 className="w-3.5 h-3.5" />,
  reporting: <FileText className="w-3.5 h-3.5" />,
  system: <Settings className="w-3.5 h-3.5" />,
};

// ============================================================================
// COMMAND PALETTE COMPONENT
// ============================================================================
const starterPrompts: IntentSuggestion[] = [
  { text: 'Reduce duration by 1 year', category: 'Duration' },
  { text: 'Increase yield by 50 basis points', category: 'Yield' },
  { text: 'Reduce tech sector to 10%', category: 'Sector' },
  { text: 'Harvest $500K in tax losses', category: 'Tax' },
  { text: 'Hedge against 50bps rate increase', category: 'Hedging' },
];

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  isOpen,
  onClose,
  onExecute,
  onIntentResult,
  recentCommands = [],
  customActions = [],
  portfolios = [],
}) => {
  // Core state
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedCommand, setSelectedCommand] = useState<CommandAction | null>(null);
  const [params, setParams] = useState<Record<string, any>>({});
  const [isExecuting, setIsExecuting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // AI Intent mode state
  const [mode, setMode] = useState<PaletteMode>('command');
  const [intentText, setIntentText] = useState('');
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<number>(portfolios[0]?.id || 0);
  const [isProcessingIntent, setIsProcessingIntent] = useState(false);
  const [suggestions, setSuggestions] = useState<IntentSuggestion[]>(starterPrompts);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsLabel, setSuggestionsLabel] = useState('Starter Prompts');
  const [intentError, setIntentError] = useState<string | null>(null);
  const intentInputRef = useRef<HTMLTextAreaElement>(null);

  // Combine default and custom commands
  const allCommands = useMemo(() => {
    const commands = [...defaultCommands, ...customActions.map(a => ({
      ...a,
      action: undefined as unknown as () => void
    }))];
    return commands;
  }, [customActions]);

  // Filter commands based on query
  const filteredCommands = useMemo(() => {
    if (!query.trim()) {
      const recent = recentCommands
        .map(id => allCommands.find(c => c.id === id))
        .filter(Boolean) as typeof allCommands;
      return [...recent, ...allCommands.filter(c => !recentCommands.includes(c.id))];
    }

    const lowerQuery = query.toLowerCase();
    return allCommands.filter(cmd =>
      cmd.title.toLowerCase().includes(lowerQuery) ||
      cmd.description.toLowerCase().includes(lowerQuery) ||
      cmd.keywords.some(k => k.toLowerCase().includes(lowerQuery))
    ).sort((a, b) => {
      const aTitle = a.title.toLowerCase().includes(lowerQuery);
      const bTitle = b.title.toLowerCase().includes(lowerQuery);
      if (aTitle && !bTitle) return -1;
      if (!aTitle && bTitle) return 1;
      return 0;
    });
  }, [query, allCommands, recentCommands]);

  // Group by category
  const groupedCommands = useMemo(() => {
    const groups: Record<CommandCategory, typeof filteredCommands> = {
      workflow: [],
      trading: [],
      compliance: [],
      portfolio: [],
      ibor: [],
      'middle-office': [],
      reporting: [],
      system: [],
    };

    filteredCommands.forEach(cmd => {
      groups[cmd.category].push(cmd);
    });

    return groups;
  }, [filteredCommands]);

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setSelectedCommand(null);
      setParams({});
      setShowConfirmation(false);
      setMode('command');
      setIntentText('');
      setIsProcessingIntent(false);
      setShowSuggestions(false);
      setIntentError(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    if (portfolios.length === 0) {
      setSelectedPortfolioId(0);
      return;
    }

    const hasSelectedPortfolio = portfolios.some(portfolio => portfolio.id === selectedPortfolioId);
    if (!hasSelectedPortfolio) {
      setSelectedPortfolioId(portfolios[0].id);
    }
  }, [portfolios, selectedPortfolioId]);

  // Load suggestions when entering AI mode
  useEffect(() => {
    if (mode === 'ai') {
      setIntentError(null);
      agenticApi.getSuggestions().then((apiSuggestions) => {
        if (apiSuggestions.length > 0) {
          setSuggestions(apiSuggestions);
          setSuggestionsLabel('Suggested Prompts');
          return;
        }

        setSuggestions(starterPrompts);
        setSuggestionsLabel('Starter Prompts');
      }).catch(() => {
        setSuggestions(starterPrompts);
        setSuggestionsLabel('Starter Prompts');
      });
      setTimeout(() => intentInputRef.current?.focus(), 50);
    }
  }, [mode]);

  // Detect /ai prefix to switch modes
  useEffect(() => {
    if (query.toLowerCase().startsWith('/ai')) {
      setMode('ai');
      setQuery('');
    }
  }, [query]);

  const executeCommand = useCallback(async (cmd: CommandAction, parameters?: Record<string, any>) => {
    setIsExecuting(true);
    try {
      await onExecute(cmd, parameters || params);
      onClose();
    } catch (error) {
      console.error('Command execution failed:', error);
    } finally {
      setIsExecuting(false);
    }
  }, [onExecute, params, onClose]);

  const handleSelectCommand = useCallback((cmd: typeof allCommands[0]) => {
    // Special handling for AI Intent command
    if (cmd.id === 'ai-intent') {
      setMode('ai');
      return;
    }

    if (cmd.parameters && cmd.parameters.length > 0) {
      setSelectedCommand(cmd as CommandAction);
    } else if (cmd.requiresConfirmation) {
      setSelectedCommand(cmd as CommandAction);
      setShowConfirmation(true);
    } else {
      executeCommand(cmd as CommandAction);
    }
  }, [executeCommand]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (selectedCommand) {
          setSelectedCommand(null);
          setParams({});
        } else {
          onClose();
        }
        return;
      }

      if (selectedCommand) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, filteredCommands.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const cmd = filteredCommands[selectedIndex];
        if (cmd) handleSelectCommand(cmd);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, filteredCommands, selectedCommand, onClose, handleSelectCommand]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedEl = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      selectedEl?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  // Process AI intent
  const processIntent = useCallback(async () => {
    if (!intentText.trim() || isProcessingIntent || !selectedPortfolioId) return;

    setIsProcessingIntent(true);
    setIntentError(null);
    try {
      const bundle = await agenticApi.processIntent({
        intent: intentText.trim(),
        portfolioId: selectedPortfolioId,
      });

      if (onIntentResult) {
        onIntentResult(bundle);
        onClose();
      }
    } catch (error: any) {
      console.error('Failed to process intent:', error);
      setIntentError(error.message || 'Failed to process the requested portfolio intent.');
    } finally {
      setIsProcessingIntent(false);
    }
  }, [intentText, selectedPortfolioId, isProcessingIntent, onIntentResult, onClose]);

  const handleParamSubmit = useCallback(() => {
    if (!selectedCommand) return;

    const missingRequired = selectedCommand.parameters?.filter(
      p => p.required && !params[p.id]
    );

    if (missingRequired && missingRequired.length > 0) return;

    if (selectedCommand.requiresConfirmation) {
      setShowConfirmation(true);
    } else {
      executeCommand(selectedCommand, params);
    }
  }, [selectedCommand, params, executeCommand]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        />

        {/* Palette */}
        <motion.div
          className="relative w-full max-w-2xl mx-4 bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          transition={{ duration: 0.15 }}
        >
          {/* Mode Toggle & Search Input */}
          <div className="border-b border-white/10">
            {/* Mode Toggle Pills */}
            <div className="flex items-center gap-2 px-4 py-2 border-b border-white/5">
              <button
                onClick={() => setMode('command')}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all",
                  mode === 'command'
                    ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                    : "text-slate-400 hover:text-white hover:bg-white/5"
                )}
              >
                <Command className="w-3.5 h-3.5" />
                Commands
              </button>
              <button
                onClick={() => setMode('ai')}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all",
                  mode === 'ai'
                    ? "bg-violet-500/20 text-violet-400 border border-violet-500/30"
                    : "text-slate-400 hover:text-white hover:bg-white/5"
                )}
              >
                <Brain className="w-3.5 h-3.5" />
                AI Intent
                <span className="px-1 py-0.5 text-[9px] bg-violet-500/30 rounded">NEW</span>
              </button>
              <div className="flex-1" />
              <div className="flex items-center gap-1.5">
                <kbd className="px-1.5 py-0.5 text-[10px] font-medium bg-slate-800 text-slate-400 rounded border border-white/10">
                  ESC
                </kbd>
                <span className="text-xs text-slate-500">to close</span>
              </div>
            </div>

            {/* Command Mode Input */}
            {mode === 'command' && (
              <div className="flex items-center gap-3 px-4 py-3">
                <Search className="w-5 h-5 text-slate-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setSelectedIndex(0);
                  }}
                  placeholder={selectedCommand ? `Configure: ${selectedCommand.title}` : "Type a command or search... (type /ai for AI mode)"}
                  className="flex-1 bg-transparent text-white placeholder-slate-500 outline-none text-sm"
                  disabled={!!selectedCommand}
                />
              </div>
            )}

            {/* AI Intent Mode Input */}
            {mode === 'ai' && (
              <div className="p-4 space-y-3">
                {/* Portfolio Selector */}
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5">Portfolio</label>
                  <select
                    value={selectedPortfolioId}
                    onChange={(e) => setSelectedPortfolioId(Number(e.target.value))}
                    disabled={portfolios.length === 0}
                    className="w-full px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white text-sm outline-none focus:border-violet-500"
                  >
                    {portfolios.length === 0 ? (
                      <option value={0}>No portfolios available</option>
                    ) : (
                      portfolios.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))
                    )}
                  </select>
                </div>

                {portfolios.length === 0 && (
                  <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-400">
                    Load a portfolio before using AI Intent. This workspace no longer uses demo portfolios.
                  </div>
                )}

                {/* Intent Input */}
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5">
                    What do you want to achieve?
                  </label>
                  <div className="relative">
                    <textarea
                      ref={intentInputRef}
                      value={intentText}
                      onChange={(e) => {
                        setIntentText(e.target.value);
                        setShowSuggestions(e.target.value.length === 0);
                      }}
                      onFocus={() => setShowSuggestions(intentText.length === 0)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          processIntent();
                        }
                      }}
                      placeholder='e.g., "Reduce duration by 1 year without increasing credit risk"'
                      className="w-full px-3 py-3 bg-slate-800 border border-white/10 rounded-lg text-white text-sm outline-none focus:border-violet-500 resize-none min-h-[80px]"
                      rows={3}
                    />

                    {/* Suggestions Dropdown */}
                    {showSuggestions && suggestions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-white/10 rounded-lg overflow-hidden z-10 shadow-xl">
                        <div className="px-3 py-1.5 text-[10px] font-medium text-slate-500 uppercase tracking-wider border-b border-white/5">
                          {suggestionsLabel}
                        </div>
                        {suggestions.map((s, i) => (
                          <button
                            key={i}
                            onClick={() => {
                              setIntentText(s.text);
                              setShowSuggestions(false);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-slate-300 hover:bg-violet-500/20 hover:text-white transition-colors flex items-center gap-2"
                          >
                            <Sparkles className="w-3 h-3 text-violet-400" />
                            {s.text}
                            <span className="text-xs text-slate-500 ml-auto">{s.category}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {intentError && (
                  <div className="rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs text-red-400">
                    {intentError}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  onClick={processIntent}
                  disabled={!intentText.trim() || isProcessingIntent || !selectedPortfolioId}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-violet-600 hover:bg-violet-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
                >
                  {isProcessingIntent ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Analyzing intent...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Generate Trade Plan
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Parameter Form or Confirmation */}
          {selectedCommand && (showConfirmation ? (
            <div className="p-4 border-b border-white/10 bg-amber-500/5">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-white">Confirm Action</h4>
                  <p className="text-sm text-slate-400 mt-1">
                    Are you sure you want to execute "{selectedCommand.title}"?
                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => executeCommand(selectedCommand, params)}
                      disabled={isExecuting}
                      className="px-3 py-1.5 text-sm font-medium bg-amber-500 text-black rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-50"
                    >
                      {isExecuting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
                    </button>
                    <button
                      onClick={() => {
                        setShowConfirmation(false);
                        setSelectedCommand(null);
                      }}
                      className="px-3 py-1.5 text-sm font-medium text-slate-400 hover:text-white transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : selectedCommand.parameters && (
            <div className="p-4 border-b border-white/10 bg-blue-500/5">
              <div className="flex items-center gap-2 mb-3">
                {selectedCommand.icon}
                <span className="font-medium text-white">{selectedCommand.title}</span>
              </div>
              <div className="space-y-3">
                {selectedCommand.parameters.map(param => (
                  <div key={param.id}>
                    <label className="block text-xs text-slate-400 mb-1">
                      {param.label} {param.required && <span className="text-red-400">*</span>}
                    </label>
                    {param.type === 'select' ? (
                      <select
                        value={params[param.id] || ''}
                        onChange={(e) => setParams(p => ({ ...p, [param.id]: e.target.value }))}
                        className="w-full px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white text-sm outline-none focus:border-blue-500"
                      >
                        <option value="">Select...</option>
                        {param.options?.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={param.type === 'number' ? 'number' : param.type === 'date' ? 'date' : 'text'}
                        value={params[param.id] || ''}
                        onChange={(e) => setParams(p => ({ ...p, [param.id]: e.target.value }))}
                        placeholder={param.placeholder}
                        className="w-full px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white text-sm outline-none focus:border-blue-500"
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-4">
                <button
                  onClick={handleParamSubmit}
                  disabled={isExecuting}
                  className="px-4 py-2 text-sm font-medium bg-blue-500 text-white rounded-lg hover:bg-blue-400 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isExecuting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  Execute
                </button>
                <button
                  onClick={() => {
                    setSelectedCommand(null);
                    setParams({});
                  }}
                  className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
                >
                  Back
                </button>
              </div>
            </div>
          ))}

          {/* Command List - Only in command mode */}
          {mode === 'command' && !selectedCommand && (
            <div ref={listRef} className="max-h-[50vh] overflow-y-auto">
              {Object.entries(groupedCommands).map(([category, commands]) => {
                if (commands.length === 0) return null;

                return (
                  <div key={category}>
                    <div className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-2 bg-slate-800/50 sticky top-0">
                      {categoryIcons[category as CommandCategory]}
                      {categoryLabels[category as CommandCategory]}
                    </div>
                    {commands.map((cmd) => {
                      const globalIndex = filteredCommands.indexOf(cmd);
                      const isSelected = globalIndex === selectedIndex;

                      return (
                        <motion.div
                          key={cmd.id}
                          data-index={globalIndex}
                          className={cn(
                            "flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors",
                            isSelected ? "bg-blue-500/20" : "hover:bg-white/5"
                          )}
                          onClick={() => handleSelectCommand(cmd)}
                          onMouseEnter={() => setSelectedIndex(globalIndex)}
                        >
                          <div className={cn(
                            "p-2 rounded-lg",
                            isSelected ? "bg-blue-500/30 text-blue-300" : "bg-slate-800 text-slate-400"
                          )}>
                            {cmd.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-white">{cmd.title}</div>
                            <div className="text-xs text-slate-500 truncate">{cmd.description}</div>
                          </div>
                          {cmd.shortcut && (
                            <kbd className="px-2 py-1 text-xs font-medium bg-slate-800 text-slate-400 rounded border border-white/10">
                              {cmd.shortcut}
                            </kbd>
                          )}
                          <ChevronRight className={cn(
                            "w-4 h-4 transition-colors",
                            isSelected ? "text-blue-400" : "text-slate-600"
                          )} />
                        </motion.div>
                      );
                    })}
                  </div>
                );
              })}

              {filteredCommands.length === 0 && (
                <div className="py-12 text-center">
                  <Search className="w-8 h-8 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-500">No commands found for "{query}"</p>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="px-4 py-2 border-t border-white/10 bg-slate-800/50 flex items-center justify-between text-xs text-slate-500">
            <div className="flex items-center gap-3">
              {mode === 'command' ? (
                <>
                  <span className="flex items-center gap-1">
                    <kbd className="px-1 py-0.5 bg-slate-700 rounded text-[10px]">↑</kbd>
                    <kbd className="px-1 py-0.5 bg-slate-700 rounded text-[10px]">↓</kbd>
                    navigate
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-1 py-0.5 bg-slate-700 rounded text-[10px]">↵</kbd>
                    select
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-1 py-0.5 bg-slate-700 rounded text-[10px]">/ai</kbd>
                    AI mode
                  </span>
                </>
              ) : (
                <>
                  <span className="flex items-center gap-1">
                    <kbd className="px-1 py-0.5 bg-slate-700 rounded text-[10px]">↵</kbd>
                    submit
                  </span>
                  <span className="text-violet-400">
                    Powered by Goal-Oriented Planning
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              {mode === 'ai' ? (
                <Brain className="w-3 h-3 text-violet-400" />
              ) : (
                <BarChart3 className="w-3 h-3 text-blue-400" />
              )}
              <span>Axiom OMS</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CommandPalette;
